<template>
  <div class="payment-record-review">
    <header class="page-header">
      <div class="header-content">
        <h2>转账记录审核</h2>
        <p class="subtitle">审核学员付款记录，上传对账单并执行对账</p>
      </div>
      <div class="header-stats" v-if="paymentRecords.length">
        <div class="stat-item">
          <span class="stat-label">待审核</span>
          <span class="stat-value warning">{{ paymentRecords.filter(r => r.status === 'PENDING_REVIEW').length }}</span>
        </div>
      </div>
    </header>

    <section class="filter-panel">
      <div class="filter-item">
        <label>课程</label>
        <select v-model="filters.courseId" @change="loadPaymentRecords">
          <option value="">全部课程</option>
          <option v-for="course in courseOptions" :key="course.id" :value="course.id">
            {{ course.title }}
          </option>
        </select>
      </div>
      <div class="filter-item">
        <label>状态</label>
        <select v-model="filters.status" @change="loadPaymentRecords">
          <option value="">全部状态</option>
          <option v-for="option in statusOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
      <div class="filter-item">
        <label>关键字</label>
        <input v-model="filters.keyword" placeholder="付款人姓名 / 账号 / 学员姓名" @keyup.enter="loadPaymentRecords" />
      </div>
      <div class="filter-actions">
        <button class="primary-button" @click="loadPaymentRecords">查询</button>
        <button class="ghost-button" @click="resetFilters">重置</button>
      </div>
    </section>

    <section class="content-area">
      <!-- 按课程分组显示 -->
      <div v-for="(group, courseId) in groupedRecords" :key="courseId" class="course-group">
        <div class="course-header">
          <h3>{{ getCourseTitle(courseId) }}</h3>
          <div class="course-actions">
            <button class="ghost-button small" @click="viewBankStatements(courseId)">
              已上传对账单
              <span v-if="bankStatements[courseId] && bankStatements[courseId].length" class="badge-count">
                ({{ bankStatements[courseId].length }})
              </span>
            </button>
            <button class="primary-button small" @click="openBankStatementUpload(courseId)">
              上传对账单
            </button>
            <button class="ghost-button small" @click="compareRecords(courseId)">
              执行对账
            </button>
          </div>
        </div>

        <!-- 付款记录列表 -->
        <div class="records-section">
          <div class="records-header">
            <div class="header-left">
              <label class="checkbox-label">
                <input type="checkbox" :checked="isAllSelected(courseId)" @change="toggleSelectAll(courseId)" />
                <span class="checkbox-text">全选</span>
              </label>
              <span class="record-count">共 {{ group.length }} 条记录</span>
            </div>
            <div class="batch-actions" v-if="selectedRecords.length">
              <span class="selected-count">已选择 {{ selectedRecords.length }} 条</span>
              <select v-model="batchStatus" class="status-select">
                <option value="">选择状态</option>
                <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
              <button class="primary-button small" @click="batchUpdateStatus" :disabled="!batchStatus">
                批量更新
              </button>
            </div>
          </div>

          <table class="records-table">
            <thead>
              <tr>
                <th width="50">
                  <input type="checkbox" :checked="isAllSelected(courseId)" @change="toggleSelectAll(courseId)" />
                </th>
                <th>学员信息</th>
                <th>付款信息</th>
                <th>转账日期</th>
                <th>转账金额</th>
                <th>待支付金额</th>
                <th>状态</th>
                <th>对账结果</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="record in group" :key="record.id" :class="{ 'unmatched': record.status === 'UNMATCHED' }">
                <td>
                  <input 
                    type="checkbox" 
                    :value="record.id" 
                    v-model="selectedRecords" 
                    :disabled="record.status === 'CONFIRMED_PAID'"
                    :title="record.status === 'CONFIRMED_PAID' ? '已付款状态不允许修改' : ''"
                  />
                </td>
                <td>
                  <div class="student-info">
                    <div>{{ record.studentName }}</div>
                    <div class="text-muted">
                      {{ getDisplayValue(record.studentId, 'phone', record.studentPhone) }}
                      <button v-if="record.studentPhone" class="decrypt-btn" @click="toggleDecrypt(record.studentId, 'phone')">
                        <svg class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <template v-if="decryptedValues['STUDENT_PROFILE_' + record.studentId + '_phone']">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </template>
                          <template v-else>
                            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                            <line x1="1" y1="1" x2="23" y2="23"/>
                          </template>
                        </svg>
                      </button>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="payment-info">
                    <div>{{ record.payerName }}</div>
                    <div class="text-muted">{{ record.payerBankAccount }}</div>
                  </div>
                </td>
                <td class="date-cell">{{ formatDate(record.transferDate) }}</td>
                <td class="amount-cell">
                  <span class="amount-value">¥{{ record.transferAmount }}</span>
                </td>
                <td class="amount-cell">
                  <span class="amount-value warning" v-if="record.pendingPaymentAmount > 0">¥{{ record.pendingPaymentAmount }}</span>
                  <span class="amount-value success" v-else>已付清</span>
                </td>
                <td>
                  <span :class="['status-tag', record.status?.toLowerCase()]">
                    {{ statusLabel(record.status) }}
                  </span>
                </td>
                <td>
                  <span v-if="record.matchResult" class="match-result-text">
                    {{ getMatchResultLabel(record.matchResult) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <div class="action-buttons">
                    <button class="link-button" @click="viewDetail(record)">详情</button>
                    <button 
                      class="link-button" 
                      @click="openStatusModal(record)" 
                      :disabled="record.status === 'CONFIRMED_PAID'"
                      :title="record.status === 'CONFIRMED_PAID' ? '已付款状态不允许修改' : ''"
                    >
                      修改状态
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="!paymentRecords.length" class="empty-state">
        <div class="empty-icon">📋</div>
        <div class="empty-text">暂无付款记录</div>
        <div class="empty-hint">请使用上方筛选条件查询记录</div>
      </div>
    </section>

    <!-- 对账单上传模态框 -->
    <div v-if="showBankStatementModal" class="modal-overlay" @click="closeBankStatementModal">
      <div class="modal-content" @click.stop>
        <h3>上传对账单</h3>
        <div class="upload-area">
          <input type="file" ref="bankStatementInput" accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" @change="handleBankStatementChange" style="display: none" />
          <button class="ghost-button" @click="$refs.bankStatementInput && $refs.bankStatementInput.click()" :disabled="uploadingBankStatement">
            {{ uploadingBankStatement ? '上传中...' : '选择Excel文件' }}
          </button>
          <div class="upload-hint">支持格式：.xls, .xlsx（Sheet名称需为"交易明细"）</div>
          <div class="sample-excel-section">
            <div class="sample-excel-label">样例文件：</div>
            <a href="#" @click.prevent="downloadSampleExcel" class="sample-excel-link">
              <span class="icon">📥</span>
              下载样例Excel文件
            </a>
          </div>
          <div v-if="uploadingBankStatement" class="upload-status">正在上传并解析Excel文件，请稍候...</div>
        </div>
        <div class="modal-actions">
          <button class="ghost-button" @click="closeBankStatementModal" :disabled="uploadingBankStatement">取消</button>
        </div>
      </div>
    </div>

    <!-- 对账单列表模态框 -->
    <div v-if="showBankStatementsModal" class="modal-overlay" @click="closeBankStatementsModal">
      <div class="modal-content large" @click.stop>
        <h3>已上传对账单列表</h3>
        <div class="bank-statements-modal-content">
          <div v-if="currentBankStatements && currentBankStatements.length" class="statements-list">
            <div v-for="statement in currentBankStatements" :key="statement.id" class="statement-card">
              <div class="statement-card-header">
                <div class="statement-title">
                  <span class="icon">📄</span>
                  <span>对账单 #{{ statement.id }}</span>
                </div>
                <button class="danger-button small" @click="deleteBankStatement(statement.id)">删除</button>
              </div>
              <div class="statement-card-body">
                <div class="info-grid">
                  <div class="info-item">
                    <span class="info-label">上传时间：</span>
                    <span class="info-value">{{ formatDate(statement.uploadedAt) }}</span>
                  </div>
                  <div class="info-item" v-if="statement.uploadedByUsername">
                    <span class="info-label">上传人：</span>
                    <span class="info-value">{{ statement.uploadedByUsername }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">解析记录数：</span>
                    <span class="info-value highlight">{{ getRecordCount(statement.ocrResult) }} 条</span>
                  </div>
                </div>
                <div v-if="getParsedRecords(statement.ocrResult).length" class="parsed-records">
                  <div class="records-header">
                    <h4>解析的交易记录</h4>
                    <span class="records-count">{{ getParsedRecords(statement.ocrResult).length }} 条</span>
                  </div>
                  <div class="records-table-wrapper">
                    <table class="parsed-records-table">
                      <thead>
                        <tr>
                          <th>交易时间</th>
                          <th>付款方姓名</th>
                          <th>付款方账号</th>
                          <th>交易金额</th>
                          <th>备注</th>
                          <th>交易流水号</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(record, index) in getParsedRecords(statement.ocrResult)" :key="index">
                          <td>{{ formatTransferDate(record.transferDate) }}</td>
                          <td>{{ record.payerName || '—' }}</td>
                          <td>{{ record.payerAccount || '—' }}</td>
                          <td class="amount-cell">¥{{ record.amount || '0.00' }}</td>
                          <td>{{ record.remark || '—' }}</td>
                          <td>{{ record.transactionNumber || '—' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <div class="empty-icon">📋</div>
            <div class="empty-text">暂无对账单</div>
          </div>
        </div>
        <div class="modal-actions">
          <button class="ghost-button" @click="closeBankStatementsModal">关闭</button>
        </div>
      </div>
    </div>

    <!-- 修改状态模态框 -->
    <div v-if="showStatusModal" class="modal-overlay" @click="closeStatusModal">
      <div class="modal-content" @click.stop>
        <h3>修改状态</h3>
        <div v-if="selectedRecordForStatus" class="status-change-content">
          <div class="record-info">
            <p><strong>学员：</strong>{{ selectedRecordForStatus.studentName }}</p>
            <p><strong>当前状态：</strong>{{ statusLabel(selectedRecordForStatus.status) }}</p>
          </div>
          <div class="status-select-wrapper">
            <label>选择新状态：</label>
            <select v-model="newStatusForRecord" class="status-select">
              <option value="">请选择</option>
              <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="modal-actions">
          <button class="ghost-button" @click="closeStatusModal">取消</button>
          <button class="primary-button" @click="confirmStatusChange" :disabled="!newStatusForRecord">确认</button>
        </div>
      </div>
    </div>

    <!-- 详情模态框 -->
    <div v-if="showDetailModal" class="modal-overlay" @click="closeDetailModal">
      <div class="modal-content large" @click.stop>
        <h3>付款记录详情</h3>
        <div v-if="selectedRecord" class="detail-content">
          <div class="detail-section">
            <h4>学员信息</h4>
            <p>姓名：{{ selectedRecord.studentName }}</p>
            <p>手机号：{{ selectedRecord.studentPhone }}</p>
          </div>
          <div class="detail-section">
            <h4>付款信息</h4>
            <p>付款人姓名：{{ selectedRecord.payerName }}</p>
            <p>付款银行账号：{{ selectedRecord.payerBankAccount }}</p>
            <p>转账日期：{{ formatDate(selectedRecord.transferDate) }}</p>
            <p>转账金额：¥{{ selectedRecord.transferAmount }}</p>
            <p v-if="selectedRecord.remark">备注：{{ selectedRecord.remark }}</p>
            <p v-if="selectedRecord.transferScreenshotUrl">
              转账截图：<a :href="selectedRecord.transferScreenshotUrl" target="_blank">查看</a>
            </p>
          </div>
          <div class="detail-section" v-if="selectedRecord.matchResult">
            <h4>对账结果</h4>
            <div v-if="getMatchResultData(selectedRecord.matchResult)?.matched" class="match-result-list">
              <div class="match-info-header">
                <span class="match-status matched">已匹配</span>
                <span class="match-score" v-if="getMatchResultData(selectedRecord.matchResult)?.matchScore">
                  匹配度: {{ (getMatchResultData(selectedRecord.matchResult).matchScore * 100).toFixed(1) }}%
                </span>
              </div>
              <div v-if="getMatchResultData(selectedRecord.matchResult)?.matchedRecord" class="matched-record-info">
                <h5>匹配的银行收款信息：</h5>
                <table class="match-record-table">
                  <tbody>
                    <tr>
                      <td class="label">交易时间：</td>
                      <td>{{ formatTransferDate(getMatchResultData(selectedRecord.matchResult).matchedRecord.transferDate) }}</td>
                    </tr>
                    <tr>
                      <td class="label">付款方姓名：</td>
                      <td>{{ getMatchResultData(selectedRecord.matchResult).matchedRecord.payerName || '—' }}</td>
                    </tr>
                    <tr>
                      <td class="label">付款方账号：</td>
                      <td>{{ getMatchResultData(selectedRecord.matchResult).matchedRecord.payerAccount || '—' }}</td>
                    </tr>
                    <tr>
                      <td class="label">交易金额：</td>
                      <td class="amount">¥{{ getMatchResultData(selectedRecord.matchResult).matchedRecord.amount || '0.00' }}</td>
                    </tr>
                    <tr v-if="getMatchResultData(selectedRecord.matchResult).matchedRecord.remark">
                      <td class="label">备注：</td>
                      <td>{{ getMatchResultData(selectedRecord.matchResult).matchedRecord.remark }}</td>
                    </tr>
                    <tr v-if="getMatchResultData(selectedRecord.matchResult).matchedRecord.transactionNumber">
                      <td class="label">交易流水号：</td>
                      <td>{{ getMatchResultData(selectedRecord.matchResult).matchedRecord.transactionNumber }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="match-result-list">
              <span class="match-status unmatched">未匹配</span>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button class="ghost-button" @click="closeDetailModal">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { paymentRecordService } from '@/services/paymentRecordService'
import { courseBankStatementService } from '@/services/courseBankStatementService'
import { courseService } from '@/services/courseService'
import { sensitiveDataService } from '../services/sensitiveDataService'

const STATUS_MAP = {
  PENDING_REVIEW: '待审核',
  MATCHED: '已匹配',
  UNMATCHED: '未匹配',
  CONFIRMED_PAID: '已确认付款'
}

export default {
  name: 'PaymentRecordReview',
  data() {
    return {
      paymentRecords: [],
      bankStatements: {},
      courseOptions: [],
      filters: {
        courseId: '',
        status: '',
        keyword: ''
      },
      statusOptions: Object.entries(STATUS_MAP).map(([value, label]) => ({ value, label })),
      selectedRecords: [],
      batchStatus: '',
      showBankStatementModal: false,
      showBankStatementsModal: false,
      currentCourseId: null,
      currentBankStatements: [],
      uploadingBankStatement: false,
      showDetailModal: false,
      selectedRecord: null,
      showStatusModal: false,
      selectedRecordForStatus: null,
      newStatusForRecord: '',
      decryptedValues: {},
      pagination: {
        page: 0,
        size: 100
      }
    }
  },
  computed: {
    groupedRecords() {
      const groups = {}
      this.paymentRecords.forEach(record => {
        const courseId = record.courseId
        if (!groups[courseId]) {
          groups[courseId] = []
        }
        groups[courseId].push(record)
      })
      return groups
    },
    sampleExcelUrl() {
      return '/api/courses/bank-statements/sample'
    }
  },
  async created() {
    await this.loadCourses()
    this.loadPaymentRecords()
    this.loadAllBankStatements()
  },
  methods: {
    async loadCourses() {
      try {
        const response = await courseService.fetchCourses({ page: 0, size: 1000 })
        if (response.success) {
          this.courseOptions = response.data.content || []
        }
      } catch (error) {
        console.error('加载课程列表失败', error)
      }
    },
    async loadPaymentRecords() {
      try {
        const params = {
          page: this.pagination.page,
          size: this.pagination.size,
          courseId: this.filters.courseId || undefined,
          status: this.filters.status || undefined,
          keyword: this.filters.keyword || undefined
        }
        const response = await paymentRecordService.fetchPaymentRecords(params)
        if (response.success) {
          this.paymentRecords = response.data.content || []
        }
      } catch (error) {
        console.error('加载付款记录失败', error)
      }
    },
    async loadAllBankStatements() {
      // 加载所有课程的对账单
      try {
        // 等待课程列表加载完成
        if (this.courseOptions.length === 0) {
          await this.loadCourses()
        }
        // 为每个课程加载对账单
        for (const course of this.courseOptions) {
          await this.loadBankStatements(course.id)
        }
      } catch (error) {
        console.error('加载所有对账单失败', error)
      }
    },
    async loadBankStatements(courseId) {
      try {
        const response = await courseBankStatementService.getBankStatements(courseId)
        if (response.success) {
          // Vue 3 中可以直接赋值，响应式系统会自动处理
          this.bankStatements[courseId] = response.data || []
          console.log(`课程 ${courseId} 的对账单加载成功:`, response.data?.length || 0, '条')
        } else {
          console.warn(`课程 ${courseId} 的对账单加载失败:`, response)
          this.bankStatements[courseId] = []
        }
      } catch (error) {
        console.error(`加载课程 ${courseId} 的对账单失败`, error)
        // 即使出错也设置为空数组，避免后续判断出错
        this.bankStatements[courseId] = []
      }
    },
    resetFilters() {
      this.filters = {
        courseId: '',
        status: '',
        keyword: ''
      }
      this.loadPaymentRecords()
    },
    getCourseTitle(courseId) {
      // 尝试数字和字符串两种类型匹配
      const course = this.courseOptions.find(c => c.id === courseId || c.id === Number(courseId) || String(c.id) === String(courseId))
      if (course) {
        return `课程名称：${course.title}`
      }
      // 如果找不到课程，尝试从付款记录中获取课程名称
      const record = this.paymentRecords.find(r => r.courseId === courseId || r.courseId === Number(courseId))
      if (record && record.courseTitle) {
        return `课程名称：${record.courseTitle}`
      }
      return `课程名称：未知课程（ID: ${courseId}）`
    },
    statusLabel(status) {
      return STATUS_MAP[status] || status || '—'
    },
    formatDate(value) {
      if (!value) return '—'
      if (value.includes('T')) {
        return value.split('T')[0]
      }
      return value.substring(0, 10)
    },
    isAllSelected(courseId) {
      const group = this.groupedRecords[courseId] || []
      // 过滤掉已付款状态的记录
      const selectableRecords = group.filter(r => r.status !== 'CONFIRMED_PAID')
      return selectableRecords.length > 0 && selectableRecords.every(r => this.selectedRecords.includes(r.id))
    },
    toggleSelectAll(courseId) {
      const group = this.groupedRecords[courseId] || []
      // 过滤掉已付款状态的记录
      const selectableRecords = group.filter(r => r.status !== 'CONFIRMED_PAID')
      const allSelected = this.isAllSelected(courseId)
      if (allSelected) {
        selectableRecords.forEach(r => {
          const index = this.selectedRecords.indexOf(r.id)
          if (index > -1) {
            this.selectedRecords.splice(index, 1)
          }
        })
      } else {
        selectableRecords.forEach(r => {
          if (!this.selectedRecords.includes(r.id)) {
            this.selectedRecords.push(r.id)
          }
        })
      }
    },
    openStatusModal(record) {
      // 如果状态是已付款，不允许修改
      if (record.status === 'CONFIRMED_PAID') {
        alert('已付款状态不允许修改')
        return
      }
      this.selectedRecordForStatus = record
      this.newStatusForRecord = record.status
      this.showStatusModal = true
    },
    closeStatusModal() {
      this.showStatusModal = false
      this.selectedRecordForStatus = null
      this.newStatusForRecord = ''
    },
    async confirmStatusChange() {
      if (!this.newStatusForRecord || !this.selectedRecordForStatus) return
      try {
        await paymentRecordService.updatePaymentRecordStatus(this.selectedRecordForStatus.id, this.newStatusForRecord)
        await this.loadPaymentRecords()
        this.closeStatusModal()
        alert('状态更新成功')
      } catch (error) {
        console.error('更新状态失败', error)
        alert('更新失败: ' + (error.message || '未知错误'))
      }
    },
    async batchUpdateStatus() {
      if (!this.batchStatus || !this.selectedRecords.length) return
      try {
        await paymentRecordService.batchUpdateStatus(this.selectedRecords, this.batchStatus)
        this.selectedRecords = []
        this.batchStatus = ''
        await this.loadPaymentRecords()
      } catch (error) {
        console.error('批量更新失败', error)
        alert('批量更新失败: ' + (error.message || '未知错误'))
      }
    },
    openBankStatementUpload(courseId) {
      this.currentCourseId = courseId
      this.showBankStatementModal = true
    },
    closeBankStatementModal() {
      this.showBankStatementModal = false
      this.currentCourseId = null
    },
    async handleBankStatementChange(event) {
      const file = event.target.files[0]
      if (!file) {
        // 重置input，允许再次选择同一文件
        event.target.value = ''
        return
      }
      
      // 验证文件格式
      const fileName = file.name.toLowerCase()
      if (!fileName.endsWith('.xls') && !fileName.endsWith('.xlsx')) {
        alert('文件格式不正确，请上传Excel文件（.xls或.xlsx）')
        event.target.value = ''
        return
      }
      
      try {
        this.uploadingBankStatement = true
        const response = await courseBankStatementService.uploadBankStatement(this.currentCourseId, file)
        console.log('上传响应:', response)
        
        if (response && response.success) {
          const recordCount = response.data?.recordCount || 0
          alert('上传成功！已解析 ' + recordCount + ' 条记录')
          await this.loadBankStatements(this.currentCourseId)
          await this.loadPaymentRecords() // 重新加载付款记录列表
          this.closeBankStatementModal()
        } else {
          // 提取错误信息 - 优先使用error字段，然后是message字段
          const errorMsg = response?.error || response?.message || response?.data?.message || '未知错误'
          console.error('上传失败响应:', response)
          alert('上传失败: ' + errorMsg)
        }
      } catch (error) {
        console.error('上传对账单失败', error)
        console.error('错误详情:', {
          message: error.message,
          response: error.response,
          data: error.response?.data
        })
        
        // 尝试从多个位置提取错误信息
        let errorMessage = '未知错误'
        if (error.response && error.response.data) {
          // 优先使用error字段，然后是message字段
          if (error.response.data.error) {
            errorMessage = error.response.data.error
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message
          } else if (typeof error.response.data === 'string') {
            errorMessage = error.response.data
          }
        } else if (error.message) {
          errorMessage = error.message
        }
        
        alert('上传失败: ' + errorMessage)
      } finally {
        this.uploadingBankStatement = false
        // 重置input，允许再次选择文件
        event.target.value = ''
      }
    },
    async compareRecords(courseId) {
      if (!confirm('确定要执行对账吗？这将比对所有对账单和学员付款记录。')) {
        return
      }
      try {
        const response = await paymentRecordService.comparePaymentRecords(courseId)
        if (response.success) {
          alert(`对账完成：${response.data.message}`)
          await this.loadPaymentRecords()
        }
      } catch (error) {
        console.error('对账失败', error)
        alert('对账失败: ' + (error.message || '未知错误'))
      }
    },
    async downloadSampleExcel() {
      try {
        const blob = await courseBankStatementService.downloadSampleExcel()
        // 创建blob对象并下载
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'bank-statement-sample.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('下载样例Excel文件失败', error)
        alert('下载失败: ' + (error.message || '未知错误'))
      }
    },
    async deleteBankStatement(id) {
      if (!confirm('确定要删除该对账单吗？')) {
        return
      }
      try {
        await courseBankStatementService.deleteBankStatement(id)
        // 重新加载当前课程的对账单列表
        if (this.currentCourseId) {
          await this.loadBankStatements(this.currentCourseId)
          this.currentBankStatements = this.bankStatements[this.currentCourseId] || []
        }
        // 同时重新加载付款记录（因为对账单变化可能影响对账结果）
        await this.loadPaymentRecords()
        alert('删除成功')
      } catch (error) {
        console.error('删除对账单失败', error)
        alert('删除失败: ' + (error.message || '未知错误'))
      }
    },
    getRecordCount(ocrResult) {
      if (!ocrResult) return 0
      try {
        const records = JSON.parse(ocrResult)
        return Array.isArray(records) ? records.length : 0
      } catch {
        return 0
      }
    },
    getParsedRecords(ocrResult) {
      if (!ocrResult) return []
      try {
        const records = JSON.parse(ocrResult)
        return Array.isArray(records) ? records : []
      } catch {
        return []
      }
    },
    formatTransferDate(dateStr) {
      if (!dateStr) return '—'
      // 处理日期格式，可能是 "2025-04-08" 或 "2025-04-08T22:18:17" 等格式
      if (dateStr.includes('T')) {
        return dateStr.split('T')[0]
      }
      return dateStr.substring(0, 10)
    },
    async viewBankStatements(courseId) {
      this.currentCourseId = courseId
      // 确保对账单数据已加载
      if (!this.bankStatements[courseId]) {
        await this.loadBankStatements(courseId)
      }
      this.currentBankStatements = this.bankStatements[courseId] || []
      this.showBankStatementsModal = true
    },
    closeBankStatementsModal() {
      this.showBankStatementsModal = false
      this.currentCourseId = null
      this.currentBankStatements = []
    },
    getMatchResultLabel(matchResult) {
      if (!matchResult) return '—'
      try {
        const result = JSON.parse(matchResult)
        return result.matched ? '已匹配' : '未匹配'
      } catch {
        return '—'
      }
    },
    viewDetail(record) {
      this.selectedRecord = record
      this.showDetailModal = true
    },
    closeDetailModal() {
      this.showDetailModal = false
      this.selectedRecord = null
    },
    formatMatchResult(matchResult) {
      if (!matchResult) return '—'
      try {
        return JSON.stringify(JSON.parse(matchResult), null, 2)
      } catch {
        return matchResult
      }
    },
    getMatchResultData(matchResult) {
      if (!matchResult) return null
      try {
        return JSON.parse(matchResult)
      } catch {
        return null
      }
    },
    async toggleDecrypt(entityId, field, entityType = 'STUDENT_PROFILE') {
      const key = `${entityType}_${entityId}_${field}`
      if (this.decryptedValues[key]) {
        delete this.decryptedValues[key]
        return
      }
      try {
        const value = await sensitiveDataService.decryptField(entityType, entityId, field)
        if (value !== undefined && value !== null) {
          this.decryptedValues[key] = value
        }
      } catch (error) {
        console.error('解密失败:', error)
        this.$message ? this.$message.error('解密失败：' + (error.message || '未知错误')) : alert('解密失败：' + (error.message || '未知错误'))
      }
    },
    getDisplayValue(entityId, field, maskedValue, entityType = 'STUDENT_PROFILE') {
      const key = `${entityType}_${entityId}_${field}`
      return this.decryptedValues[key] || maskedValue
    }
  }
}
</script>

<style scoped>
.payment-record-review {
  padding: 24px;
  background: #f8f9fa;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.header-content {
  flex: 1;
}

.page-header h2 {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 600;
}

.subtitle {
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-size: 14px;
}

.header-stats {
  display: flex;
  gap: 24px;
  align-items: center;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  backdrop-filter: blur(10px);
}

.stat-label {
  font-size: 12px;
  opacity: 0.9;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
}

.stat-value.warning {
  color: #ffc107;
}

.filter-panel {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  padding: 20px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  flex-wrap: wrap;
  align-items: flex-end;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-item {
  flex: 1;
  min-width: 180px;
}

.filter-item label {
  font-size: 13px;
  color: #495057;
  font-weight: 500;
  margin-bottom: 6px;
}

.filter-item input,
.filter-item select {
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
  width: 100%;
  box-sizing: border-box;
  height: 42px; /* Ensure consistent height */
}

.filter-item input:focus,
.filter-item select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.filter-actions {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.course-group {
  margin-bottom: 32px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s;
}

.course-group:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.course-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e9ecef;
}

.course-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #212529;
}

.course-actions {
  display: flex;
  gap: 10px;
}

.bank-statements-section {
  margin-bottom: 20px !important;
  padding: 15px !important;
  background: #ffffff !important;
  border-radius: 4px !important;
  border: 10px solid #000000 !important;
  box-shadow: 0 0 0 3px #000000 !important;
  display: block !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

.bank-statements-header {
  margin-bottom: 16px;
}

.bank-statements-section h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  display: flex;
  align-items: center;
  gap: 8px;
}

.bank-statements-section h4 .icon {
  font-size: 20px;
}

.bank-statements-section h4 .badge {
  background: #007bff;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.bank-statements-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.statement-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: all 0.2s;
}

.statement-item:hover {
  background: #e9ecef;
  border-color: #dee2e6;
}

.statement-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 13px;
  color: #6c757d;
  font-weight: 500;
}

.info-value {
  font-size: 14px;
  color: #212529;
}

.info-value.highlight {
  color: #007bff;
  font-weight: 600;
}

.danger-button {
  background: #dc3545;
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.danger-button:hover {
  background: #c82333;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
}

.records-section {
  margin-top: 20px;
}

.records-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.checkbox-text {
  font-size: 14px;
  font-weight: 500;
  color: #495057;
}

.record-count {
  font-size: 13px;
  color: #6c757d;
  padding-left: 16px;
  border-left: 1px solid #dee2e6;
}

.selected-count {
  font-size: 13px;
  color: #007bff;
  font-weight: 500;
  padding: 0 12px;
}

.batch-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.status-select {
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.status-select.small {
  padding: 3px 8px;
  font-size: 12px;
}

.records-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.records-table th,
.records-table td {
  padding: 14px 16px;
  text-align: left;
  border-bottom: 1px solid #e9ecef;
}

.records-table th {
  background: #f8f9fa;
  font-weight: 600;
  font-size: 13px;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #dee2e6;
}

.records-table tbody tr {
  transition: background-color 0.15s;
}

.records-table tbody tr:hover {
  background: #f8f9fa;
}

.records-table tbody tr:last-child td {
  border-bottom: none;
}

.records-table tr.unmatched {
  background: #fff3cd;
}

.records-table tr.unmatched:hover {
  background: #ffeaa7;
}

.date-cell {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #495057;
}

.amount-cell {
  font-weight: 600;
}

.amount-value {
  color: #28a745;
  font-size: 15px;
}

.student-info,
.payment-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.text-muted {
  color: #999;
  font-size: 12px;
}

.status-tag {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  display: inline-block;
  white-space: nowrap;
}

.status-tag.pending_review {
  background: #fff3cd;
  color: #856404;
}

.status-tag.matched {
  background: #d4edda;
  color: #155724;
}

.status-tag.unmatched {
  background: #f8d7da;
  color: #721c24;
}

.status-tag.confirmed_paid {
  background: #d1ecf1;
  color: #0c5460;
}

.match-result-text {
  color: #495057;
}

.action-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
}

.link-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.empty-state {
  text-align: center;
  padding: 60px 40px;
  background: #ffffff;
  border-radius: 12px;
  border: 2px dashed #dee2e6;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-text {
  font-size: 18px;
  color: #495057;
  font-weight: 500;
  margin-bottom: 8px;
}

.empty-hint {
  font-size: 14px;
  color: #6c757d;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-content.large {
  max-width: 800px;
}

.modal-content h3 {
  margin: 0 0 20px 0;
}

.upload-area {
  margin-bottom: 20px;
}

.upload-status {
  margin-left: 10px;
  color: #666;
}

.upload-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #6c757d;
}

.sample-excel-section {
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.sample-excel-label {
  font-size: 13px;
  color: #495057;
  font-weight: 500;
}

.sample-excel-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #007bff;
  text-decoration: none;
  font-size: 13px;
  padding: 6px 12px;
  border: 1px solid #007bff;
  border-radius: 4px;
  transition: all 0.2s;
}

.sample-excel-link:hover {
  background: #007bff;
  color: white;
  text-decoration: none;
}

.sample-excel-link .icon {
  font-size: 14px;
}

.detail-content {
  margin-bottom: 20px;
}

.detail-section {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.detail-section:last-child {
  border-bottom: none;
}

.detail-section h4 {
  margin: 0 0 10px 0;
  color: #333;
}

.detail-section p {
  margin: 5px 0;
}

.detail-section pre {
  background: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
}

.match-result-list {
  margin-top: 12px;
}

.match-info-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
}

.match-status {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
}

.match-status.matched {
  background: #d4edda;
  color: #155724;
}

.match-status.unmatched {
  background: #f8d7da;
  color: #721c24;
}

.match-score {
  font-size: 13px;
  color: #6c757d;
}

.matched-record-info {
  margin-top: 16px;
}

.matched-record-info h5 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
}

.match-record-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.match-record-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.match-record-table tbody tr:last-child {
  border-bottom: none;
}

.match-record-table td {
  padding: 8px 0;
  vertical-align: top;
  text-align: left;
}

.match-record-table td.label {
  width: 120px;
  color: #6c757d;
  font-weight: 500;
}

.match-record-table td.amount {
  color: #28a745;
  font-weight: 600;
}

/* 对账单列表模态框样式 */
.bank-statements-modal-content {
  max-height: 70vh;
  overflow-y: auto;
}

.statements-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.statement-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  background: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s;
}

.statement-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.statement-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
}

.statement-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #212529;
}

.statement-title .icon {
  font-size: 18px;
}

.statement-card-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-item .info-label {
  font-size: 13px;
  color: #6c757d;
  font-weight: 500;
  min-width: 80px;
}

.info-item .info-value {
  font-size: 14px;
  color: #212529;
}

.info-item .info-value.highlight {
  color: #007bff;
  font-weight: 600;
}

.info-item .link-button {
  color: #007bff;
  text-decoration: none;
  font-size: 14px;
}

.info-item .link-button:hover {
  text-decoration: underline;
}

.parsed-records {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.records-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.records-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
}

.records-count {
  font-size: 12px;
  color: #6c757d;
  background: #f8f9fa;
  padding: 4px 8px;
  border-radius: 4px;
}

.records-table-wrapper {
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 4px;
}

.parsed-records-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.parsed-records-table thead {
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
}

.parsed-records-table th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.parsed-records-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e9ecef;
  color: #212529;
  text-align: left;
  text-align: left;
}

.parsed-records-table tbody tr:hover {
  background: #f8f9fa;
}

.parsed-records-table tbody tr:last-child td {
  border-bottom: none;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.primary-button,
.ghost-button,
.danger-link,
.link-button {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 14px;
}

.primary-button {
  background: #007bff;
  color: white;
  transition: all 0.2s;
  font-weight: 500;
}

.primary-button:hover {
  background: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.primary-button:disabled {
  background: #6c757d;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.primary-button.small {
  padding: 6px 14px;
  font-size: 13px;
}

.ghost-button {
  background: white;
  border: 1px solid #dee2e6;
  color: #495057;
  transition: all 0.2s;
  font-weight: 500;
}

.ghost-button:hover {
  background: #f8f9fa;
  border-color: #adb5bd;
  transform: translateY(-1px);
}

.ghost-button.small {
  padding: 6px 14px;
  font-size: 13px;
}

.badge-count {
  margin-left: 4px;
  font-size: 12px;
  color: #007bff;
  font-weight: 600;
}

.danger-link {
  background: none;
  color: #dc3545;
  text-decoration: underline;
}

.danger-link.small {
  font-size: 12px;
}

.link-button {
  background: none;
  color: #007bff;
  text-decoration: underline;
  padding: 0;
}

.decrypt-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  margin-left: 4px;
  opacity: 0.5;
  vertical-align: middle;
}
.decrypt-btn:hover { opacity: 1; color: #409EFF; }
.decrypt-btn .eye-icon { width: 16px; height: 16px; }
</style>

<style>
/* 全局样式，确保对账单容器边框生效 */
.bank-statements-section {
  background: #ffffff !important;
  border: 10px solid #000000 !important;
  box-shadow: 0 0 0 3px #000000 !important;
  margin-bottom: 20px !important;
  padding: 15px !important;
  border-radius: 4px !important;
  display: block !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

.status-change-content {
  margin-bottom: 20px;
}

.record-info {
  margin-bottom: 20px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.record-info p {
  margin: 8px 0;
  font-size: 14px;
}

.status-select-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.status-select-wrapper label {
  font-size: 14px;
  font-weight: 500;
  color: #495057;
}

.status-select-wrapper .status-select {
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
}
</style>

